/**
 * Turn an API error into something a person can read.
 *
 * The money endpoints answer with `detail` as an object, like
 * { error: "insufficient_funds", message: "...", required: 12.24 }, while the
 * older ones answer with `detail` as a plain string. Dropping either straight
 * into JSX gives you "[object Object]" for half of them, which is how a page
 * ends up showing nothing useful at the exact moment something went wrong.
 *
 * This takes whatever came back and returns { message, code, fields }.
 */

/** Extract a readable message from a parsed error body. */
export function apiErrorMessage(body, fallback = 'Something went wrong.') {
  if (!body) return fallback;
  if (typeof body === 'string') return body;

  const detail = body.detail !== undefined ? body.detail : body;
  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    // FastAPI validation errors: [{ loc: [...], msg: "..." }]
    const parts = detail
      .map((d) => (d && d.msg ? `${(d.loc || []).slice(-1)[0] || ''} ${d.msg}`.trim() : null))
      .filter(Boolean);
    if (parts.length) return parts.join('. ');
  }

  if (detail && typeof detail === 'object') {
    if (detail.message) return detail.message;
    if (detail.error) return String(detail.error).replace(/_/g, ' ');
  }
  return fallback;
}

/** The machine-readable code, when the endpoint sent one. */
export function apiErrorCode(body) {
  const detail = body && body.detail !== undefined ? body.detail : body;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail.error || null;
  }
  return null;
}

/**
 * Everything a component needs, in one call.
 * `status` is the HTTP status, used only to add context the body did not give.
 */
export function readApiError(status, body, fallback) {
  const code = apiErrorCode(body);
  const detail = body && body.detail !== undefined ? body.detail : body;
  const extras = {};
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    for (const k of ['required', 'available', 'gateway', 'charged', 'what_is_missing']) {
      if (detail[k] !== undefined) extras[k] = detail[k];
    }
  }
  return {
    status,
    code,
    message: apiErrorMessage(body, fallback),
    extras,
  };
}
